/**
 * Material und Maschinen im Arbeitsplan (Etappe 6b; Benjamin, 02.10.2026:
 * „Lieferanten und Materialien müssen sich ebenso einfach aus dem Angebot managen
 * lassen, in der Planung").
 *
 * Dieselben Zeilen wie unter „Material & Lieferanten" (`MaterialZeile`: Menge,
 * Lieferant wechseln, Stand, Vergleich, bestellt/geliefert), dazu der Liefertag
 * aus dem Plan. „Aus dem Angebot übernehmen" öffnet die vorhandene Übernahme mit
 * den Katalogvorschlägen; die Anfrage nimmt den Liefertag als Wunschtermin.
 */
import { Fragment, useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";

import AngebotUebernahme from "../projekt/AngebotUebernahme";
import AnfrageDialog, { type AnfrageStart } from "../projekt/AnfrageDialog";
import MaterialZeile from "../projekt/MaterialZeile";
import { standVon } from "../projekt/AnfrageStand";
import { useProjektMaterial } from "../../hooks/useLieferanten";
import { tagKurzWochentag, type Arbeitsplan } from "../../lib/arbeitsplan";
import "../../styles/lieferanten.css";

export default function MaterialBlock({ plan }: { plan: Arbeitsplan }) {
  const qc = useQueryClient();
  const { data: d, dataUpdatedAt } = useProjektMaterial(plan.projekt_id);
  const [stand, setStand] = useState("");
  const [uebernahme, setUebernahme] = useState(false);
  const [anfrage, setAnfrage] = useState<AnfrageStart | null>(null);

  /* Ändert sich das Material (Lieferant, Übernahme), rechnet der Plan seine
     Vorschläge neu — sie hängen am Projektmaterial. */
  useEffect(() => {
    if (dataUpdatedAt) qc.invalidateQueries({ queryKey: ["projekt", plan.projekt_id, "arbeitsplan"] });
  }, [dataUpdatedAt, plan.projekt_id, qc]);

  const materialien = d?.materialien || [];
  const liefertag = (id: string) => plan.material.find((m) => m.id === id)?.tage || [];
  const tagText = (id: string) => {
    const t = liefertag(id);
    if (!t.length) return "noch an keinem Tag";
    return t.map((x) => `Tag ${x.plan_nr ?? "?"}${x.datum ? ` · ${tagKurzWochentag(x.datum)}` : ""}`).join(", ");
  };
  const angebotOffen = d?.angebot_offen || 0;
  /* Anfragen aus der Planung (03.10.2026): was noch nicht angefragt ist, steht im
     Anfrage-Fenster vorgewählt — je Lieferant eine Mail, jede Position mit ihrem
     Liefertag aus dem Plan (material_anfrage._liefertage). */
  const offen = materialien.filter((m) => standVon(m).art === "offen");

  return (
    <div className="apl-material">
      <div className="apl-material-kopf">
        <div className="apl-zeile-text">
          <strong>Material &amp; Maschinen</strong>
          <small>
            {materialien.length === 0 ? "Noch kein Material am Projekt." : `${materialien.length} am Projekt`}
            {angebotOffen > 0 && ` · ${angebotOffen} aus dem Angebot noch nicht übernommen`}
          </small>
        </div>
        <div className="apl-material-knoepfe">
          <button type="button" className={"vg-btn" + (angebotOffen > 0 || materialien.length === 0 ? " vg-btn-primary" : "")}
            onClick={() => setUebernahme(true)}>
            Aus dem Angebot übernehmen
          </button>
          {materialien.length > 0 && (
            <button type="button" className={"vg-btn" + (offen.length > 0 && angebotOffen === 0 ? " vg-btn-primary" : "")}
              onClick={() => setAnfrage({ pmIds: offen.map((m) => m.id) })}
              title={offen.length ? `${offen.map((m) => m.name).join(", ")} — je Lieferant eine Mail, mit Liefertag aus dem Plan` : "Weitere Lieferanten anfragen oder Preise vergleichen"}>
              {offen.length ? `Material anfragen (${offen.length})` : "Material anfragen"}
            </button>
          )}
        </div>
      </div>
      {materialien.length > 0 && (
        <ul className="lf-liste lf-materialansicht">
          {materialien.map((m) => (
            <Fragment key={m.id}>
              <MaterialZeile
                m={m} projektId={plan.projekt_id} standOffen={stand === m.id} mitLieferant
                onStand={() => setStand(stand === m.id ? "" : m.id)}
                onAnfrage={() => {
                  const t = liefertag(m.id).find((x) => x.datum);
                  setAnfrage({ pmIds: [m.id], terminId: t?.tag_id });
                }}
              />
              <li className="apl-liefertag">{m.art === "maschine" ? "Einsatz" : "Liefertag"}: {tagText(m.id)}</li>
            </Fragment>
          ))}
        </ul>
      )}
      {uebernahme && (
        <AngebotUebernahme
          projektId={plan.projekt_id}
          onClose={() => { setUebernahme(false); qc.invalidateQueries({ queryKey: ["projekt", plan.projekt_id] }); }}
          onAnfragen={(pmIds) => setAnfrage({ pmIds })}
        />
      )}
      {anfrage && <AnfrageDialog projektId={plan.projekt_id} start={anfrage} onClose={() => setAnfrage(null)} />}
    </div>
  );
}
